import React, { useState, useEffect } from 'react';
import { ArrowRight, CheckCircle2, Mail, MessageSquare, Clock, BookOpen, Sparkles, Shield } from 'lucide-react';
import { waitlistAPI } from '../lib/api';
import kivoLogo from '../assets/logo/kivo-logo-transparent.png';
import kivoIcon from '../assets/logo/kivo-icon-only.png';

const WAITLIST_CAP = 50;
const SEED_COUNT = 26; // Starting display count

export const WaitlistLanding = () => {
  const [count, setCount] = useState(SEED_COUNT);
  const [form, setForm] = useState({ name: '', whatsapp_number: '', child_school: '', child_age: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [spotNumber, setSpotNumber] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    waitlistAPI.getCount()
      .then(res => {
        const real = res.data?.count || 0;
        setCount(Math.max(real, SEED_COUNT));
      })
      .catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const res = await waitlistAPI.join(form);
      if (res.data?.ok) {
        setSubmitted(true);
        setSpotNumber(Math.max(res.data.spot_number, SEED_COUNT + 1));
        setCount(prev => prev + 1);
      } else {
        setError(res.data?.error || 'Something went wrong.');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const spotsLeft = WAITLIST_CAP - count;

  return (
    <div className="min-h-screen bg-warm-cream font-sans">

      {/* ══════════════ NAV ══════════════ */}
      <nav className="max-w-5xl mx-auto px-6 py-5 flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <img src={kivoIcon} alt="Kivo" className="h-8 w-auto" />
          <span className="text-lg font-heading text-stone-900 tracking-tight">Kivo</span>
        </div>
        <a
          href="https://wa.me/919217630049"
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-stone-500 hover:text-primary transition-colors"
        >
          WhatsApp us
        </a>
      </nav>

      {/* ══════════════ HERO ══════════════ */}
      <section className="max-w-3xl mx-auto px-6 pt-12 pb-16 md:pt-20 md:pb-24 text-center">
        {/* Urgency badge */}
        <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-secondary-light rounded-full mb-8">
          <span className="w-2 h-2 rounded-full bg-secondary animate-pulse" />
          <span className="text-sm font-medium text-stone-700">
            {spotsLeft > 0 ? `${spotsLeft} spots left for founding families` : 'Waitlist is full'}
          </span>
        </div>

        <h1 className="text-4xl md:text-5xl lg:text-[3.5rem] font-heading text-stone-900 tracking-tight leading-[1.15] mb-5">
          3 PM. Is she home yet?
        </h1>

        <p className="text-lg md:text-xl text-stone-500 max-w-xl mx-auto mb-10 leading-relaxed">
          Kivo checks in with your caregiver on WhatsApp, reads school emails, guides daily activities,
          and sends you a calm evening summary. Every school day.
        </p>

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <a
            href="#waitlist"
            className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-primary text-white rounded-full font-medium hover:bg-primary-800 transition-colors shadow-sm"
          >
            Join the waitlist
            <ArrowRight className="w-4 h-4" />
          </a>
          <a
            href="#how-it-works"
            className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-white text-stone-700 border border-stone-200 rounded-full font-medium hover:bg-stone-50 transition-colors"
          >
            See how it works
          </a>
        </div>

        <p className="text-xs text-stone-400 mt-6">
          No app needed for your caregiver. Everything happens on WhatsApp.
        </p>
      </section>

      {/* ══════════════ WHATSAPP DEMO — Two Phones ══════════════ */}
      <section className="bg-white border-y border-stone-100">
        <div className="max-w-4xl mx-auto px-6 py-16 md:py-20">
          <h2 className="text-2xl md:text-3xl font-heading text-stone-900 text-center mb-3">
            What your day looks like with Kivo
          </h2>
          <p className="text-sm text-stone-500 text-center mb-12 max-w-md mx-auto">
            Your caregiver gets clear instructions. You get peace of mind.
          </p>

          <div className="grid md:grid-cols-2 gap-8 max-w-3xl mx-auto">
            {/* Caregiver phone */}
            <div>
              <p className="text-xs font-medium text-stone-400 uppercase tracking-wider mb-3 text-center">
                Your caregiver sees
              </p>
              <PhoneMockup>
                <WAHeader name="Kivo" />
                <WABubble direction="incoming" time="7:30 AM">
                  Good morning Kajal ji! ☀️{'\n\n'}
                  Arya ka aaj ka plan:{'\n'}
                  📋 Bag mein: Math notebook + water bottle{'\n'}
                  📝 Permission slip submit karna hai{'\n'}
                  🎨 Activity: Colour mixing — 2 paint colours mix karke naya colour banao{'\n\n'}
                  Sab pack ho jaye toh 👍 bhej do!
                </WABubble>
                <WABubble direction="outgoing" time="7:45 AM">
                  👍
                </WABubble>
                <WABubble direction="incoming" time="4:30 PM">
                  🍱 Arya ka khana? Batao{'\n'}
                  Full / Half / Low{'\n\n'}
                  🎨 Aaj ka activity{'\n'}
                  <strong>Haath ke Saaye</strong>{'\n'}
                  Torch light wall pe daalo, haath se shapes banao{'\n'}
                  ⏱️ 15 min{'\n\n'}
                  Reply: Done / Skip
                </WABubble>
              </PhoneMockup>
            </div>

            {/* Parent phone */}
            <div>
              <p className="text-xs font-medium text-stone-400 uppercase tracking-wider mb-3 text-center">
                You see
              </p>
              <PhoneMockup>
                <WAHeader name="Kivo" />
                <WABubble direction="incoming" time="3:15 PM" variant="parent">
                  🏠 Arya reached home at 3:15. Happy today.
                </WABubble>
                <WABubble direction="incoming" time="9:00 PM" variant="parent">
                  <strong>Arya's day — Tuesday, 25 March</strong>{'\n\n'}
                  Home at 3:15. Kajal said she came back happy, ate her full lunch. Permission slip was handed to the teacher.{'\n\n'}
                  This afternoon, Kajal guided her through shadow play with a torch — she stayed with it for 15 minutes, which is strong focus for her age.{'\n\n'}
                  <span className="text-stone-400 text-[13px]">
                    She's been responding well to hands-on creative work. You might enjoy doing something similar together this weekend.
                  </span>
                </WABubble>
              </PhoneMockup>
            </div>
          </div>

          <p className="text-center text-xs text-stone-400 mt-10">
            You didn't call anyone. You didn't check email. You just knew.
          </p>
        </div>
      </section>

      {/* ══════════════ HOW IT WORKS — 3 Steps ══════════════ */}
      <section id="how-it-works" className="max-w-4xl mx-auto px-6 py-16 md:py-20">
        <h2 className="text-2xl md:text-3xl font-heading text-stone-900 text-center mb-3">
          Set up in 5 minutes
        </h2>
        <p className="text-sm text-stone-500 text-center mb-12 max-w-md mx-auto">
          Three steps. No app download for your caregiver.
        </p>

        <div className="grid md:grid-cols-3 gap-6">
          {[
            {
              step: '1',
              icon: Mail,
              title: 'Connect your school Gmail',
              desc: 'We only read emails from your school. Takes 2 minutes.',
            },
            {
              step: '2',
              icon: MessageSquare,
              title: "Add your caregiver's WhatsApp",
              desc: 'She gets daily plans and check-ins. No app needed.',
            },
            {
              step: '3',
              icon: Clock,
              title: 'Get your evening summary',
              desc: 'At 9 PM every school day, everything in one message.',
            },
          ].map(({ step, icon: Icon, title, desc }) => (
            <div key={step} className="bg-white rounded-2xl border border-stone-100 p-6 text-center">
              <div className="w-10 h-10 rounded-full bg-primary-50 text-primary font-heading text-lg flex items-center justify-center mx-auto mb-4">
                {step}
              </div>
              <Icon className="w-5 h-5 text-primary mx-auto mb-3" />
              <h3 className="text-sm font-semibold text-stone-900 mb-1.5">{title}</h3>
              <p className="text-sm text-stone-500 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ══════════════ ACTIVITY VALUE — Kinedu-inspired ══════════════ */}
      <section className="bg-white border-y border-stone-100">
        <div className="max-w-4xl mx-auto px-6 py-16 md:py-20">
          <h2 className="text-2xl md:text-3xl font-heading text-stone-900 text-center mb-3">
            Guided activities, every afternoon
          </h2>
          <p className="text-sm text-stone-500 text-center mb-4 max-w-lg mx-auto">
            While you're at work, Kivo sends your caregiver a simple, age-appropriate activity
            drawn from Montessori, Reggio Emilia, and early childhood development research.
          </p>
          <p className="text-xs text-stone-400 text-center mb-12 max-w-md mx-auto">
            Curated by child development specialists. Rotated daily across five developmental areas.
          </p>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 max-w-2xl mx-auto">
            {[
              { area: 'Creative', example: 'Colour mixing, collage, pattern drawing', color: 'bg-orange-50 text-orange-700' },
              { area: 'Motor', example: 'Obstacle course, clay shaping, balancing', color: 'bg-blue-50 text-blue-700' },
              { area: 'Cognitive', example: 'Sorting, puzzles, memory games', color: 'bg-purple-50 text-purple-700' },
              { area: 'Language', example: 'Story retelling, rhyme games', color: 'bg-green-50 text-green-700' },
              { area: 'Social', example: 'Turn-taking, sharing, role play', color: 'bg-pink-50 text-pink-700' },
            ].map(({ area, example, color }) => (
              <div key={area} className="rounded-xl bg-warm-surface border border-stone-100 px-4 py-3.5 text-center">
                <span className={`inline-block text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full mb-2 ${color}`}>
                  {area}
                </span>
                <p className="text-xs text-stone-500 leading-relaxed">{example}</p>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-center gap-6 mt-10 text-xs text-stone-400">
            <span className="flex items-center gap-1.5"><BookOpen className="w-3.5 h-3.5" /> Research-backed</span>
            <span className="flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" /> Age-appropriate</span>
            <span className="flex items-center gap-1.5"><Shield className="w-3.5 h-3.5" /> No screen time</span>
          </div>
        </div>
      </section>

      {/* ══════════════ STATS ROW ══════════════ */}
      <section className="max-w-3xl mx-auto px-6 py-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          {[
            { stat: 'Rs.50/day', label: 'Less than your morning coffee' },
            { stat: '0', label: 'Apps your caregiver downloads' },
            { stat: '9 PM', label: 'Your daily summary, every school day' },
            { stat: '5 min', label: 'Setup time' },
          ].map(({ stat, label }) => (
            <div key={label}>
              <p className="text-2xl font-heading text-stone-900">{stat}</p>
              <p className="text-xs text-stone-400 mt-1">{label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ══════════════ WAITLIST FORM ══════════════ */}
      <section id="waitlist" className="bg-white border-y border-stone-100">
        <div className="max-w-lg mx-auto px-6 py-16 md:py-20">
          <h2 className="text-2xl md:text-3xl font-heading text-stone-900 text-center mb-3">
            Join the founding families
          </h2>
          <p className="text-sm text-stone-500 text-center mb-2">
            Limited to {WAITLIST_CAP} families. Founding members get their first month free.
          </p>

          {/* Progress bar */}
          <div className="max-w-xs mx-auto mb-8">
            <div className="flex justify-between text-xs text-stone-400 mb-1.5">
              <span>{count} joined</span>
              <span>{WAITLIST_CAP} spots</span>
            </div>
            <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-primary rounded-full transition-all duration-500"
                style={{ width: `${Math.min((count / WAITLIST_CAP) * 100, 100)}%` }}
              />
            </div>
          </div>

          {submitted ? (
            <div className="text-center py-8">
              <CheckCircle2 className="w-12 h-12 text-primary mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-stone-900 mb-2">
                You're #{spotNumber} on the list!
              </h3>
              <p className="text-sm text-stone-500 mb-6">
                We'll reach out on WhatsApp when your spot opens.
              </p>
              <a
                href={`https://wa.me/?text=${encodeURIComponent("I just joined the Kivo waitlist — an AI that manages your child's school day through WhatsApp. Check it out: https://app.kivo.family")}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-50 text-primary rounded-full text-sm font-medium hover:bg-primary-100 transition-colors"
              >
                Share with a parent friend
                <ArrowRight className="w-3.5 h-3.5" />
              </a>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-stone-600 mb-1.5">Your name</label>
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  className="w-full px-4 py-3 rounded-xl border border-stone-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  placeholder="e.g. Harshit"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-stone-600 mb-1.5">WhatsApp number</label>
                <input
                  type="tel"
                  required
                  value={form.whatsapp_number}
                  onChange={e => setForm(f => ({ ...f, whatsapp_number: e.target.value }))}
                  className="w-full px-4 py-3 rounded-xl border border-stone-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  placeholder="+91 98765 43210"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-stone-600 mb-1.5">Child's school</label>
                  <input
                    type="text"
                    value={form.child_school}
                    onChange={e => setForm(f => ({ ...f, child_school: e.target.value }))}
                    className="w-full px-4 py-3 rounded-xl border border-stone-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    placeholder="e.g. DPS R.K. Puram"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-stone-600 mb-1.5">Child's age</label>
                  <input
                    type="text"
                    value={form.child_age}
                    onChange={e => setForm(f => ({ ...f, child_age: e.target.value }))}
                    className="w-full px-4 py-3 rounded-xl border border-stone-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    placeholder="e.g. 4 years"
                  />
                </div>
              </div>

              {error && (
                <p className="text-sm text-red-600 text-center">{error}</p>
              )}

              <button
                type="submit"
                disabled={submitting || spotsLeft <= 0}
                className="w-full py-3.5 bg-primary text-white rounded-full font-medium hover:bg-primary-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Joining...' : spotsLeft > 0 ? 'Join the waitlist' : 'Waitlist is full'}
              </button>

              <p className="text-xs text-stone-400 text-center">
                We'll only message you about Kivo. No spam, ever.
              </p>
            </form>
          )}
        </div>
      </section>

      {/* ══════════════ FOOTER ══════════════ */}
      <footer className="py-8 bg-warm-cream">
        <div className="max-w-4xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img src={kivoIcon} alt="Kivo" className="h-6 w-auto" />
            <p className="text-sm text-stone-400">© 2026 Kivo. Guided childcare for Indian families.</p>
          </div>
          <div className="flex items-center gap-4">
            <a href="/privacy" className="text-xs text-stone-400 hover:text-stone-600 transition-colors">Privacy</a>
            <a href="/terms" className="text-xs text-stone-400 hover:text-stone-600 transition-colors">Terms</a>
            <a
              href="https://wa.me/919217630049"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-stone-400 hover:text-stone-600 transition-colors"
            >
              WhatsApp us
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

/* ══════════════ PHONE MOCKUP COMPONENT ══════════════ */
function PhoneMockup({ children }) {
  return (
    <div className="mx-auto max-w-[300px]">
      <div className="rounded-[2rem] border-[3px] border-stone-800 bg-stone-800 p-1 shadow-xl">
        {/* Notch */}
        <div className="mx-auto w-20 h-5 bg-stone-800 rounded-b-2xl" />
        <div className="bg-[#ECE5DD] rounded-[1.5rem] overflow-hidden">
          <div className="px-3 py-3 space-y-2 min-h-[360px] max-h-[420px] overflow-y-auto">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ══════════════ WHATSAPP HEADER ══════════════ */
function WAHeader({ name }) {
  return (
    <div className="flex items-center gap-2 px-1 py-1.5 mb-1">
      <img src={kivoIcon} alt="Kivo" className="w-7 h-7 rounded-full object-contain" />
      <span className="text-xs font-semibold text-stone-700">{name}</span>
    </div>
  );
}

/* ══════════════ WHATSAPP BUBBLE ══════════════ */
function WABubble({ direction, time, variant, children }) {
  const isOutgoing = direction === 'outgoing';
  return (
    <div className={`flex ${isOutgoing ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`rounded-xl px-3 py-2 max-w-[85%] shadow-sm ${
          isOutgoing
            ? 'bg-[#DCF8C6]'
            : variant === 'parent'
              ? 'bg-[#E8F4E8]'
              : 'bg-white'
        }`}
      >
        <p className="text-[11px] leading-relaxed text-stone-800 whitespace-pre-line">{children}</p>
        <p className="text-[9px] text-stone-400 text-right mt-0.5">{time}</p>
      </div>
    </div>
  );
}

export default WaitlistLanding;
